import { useEffect, useState, useCallback } from "react";
import { Plus, MapPin, Calendar, Image as ImageIcon, Check, X, ListChecks } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/pages/Portal";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

const STATUS = ["scheduled", "in_progress", "completed"];
const color = { scheduled: "bg-blue-100 text-blue-700", in_progress: "bg-amber-100 text-amber-700", completed: "bg-green-100 text-green-700" };
const DEFAULT_CHECKLIST = [
  "Fire extinguishers accessible & unobstructed",
  "Pressure gauge in operable (green) range",
  "Safety pin & tamper seal intact",
  "Inspection tag current & signed",
  "Smoke / heat detectors functional",
  "Alarm control panel shows no faults",
  "Emergency exit signage illuminated",
  "Sprinkler heads clear of obstruction",
  "Fire hose reel & hydrant operational",
  "Staff aware of evacuation procedure",
];
const newChecklist = () => DEFAULT_CHECKLIST.map((label) => ({ label, status: "pending" }));
const EMPTY = { title: "", client_name: "", site_address: "", equipment_type: "", scheduled_date: "", next_due_date: "", status: "scheduled", findings: "", photos: [], checklist: [] };

export default function Inspections() {
  const { user } = useAuth();
  const canEdit = user.role === "admin" || user.role === "technician";
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(() => { api.get("/inspections").then((r) => setList(r.data)); }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm({ ...EMPTY, checklist: newChecklist() }); setEditId(null); setOpen(true); };
  const openEdit = (i) => { setForm({ ...EMPTY, ...i, checklist: i.checklist?.length ? i.checklist : newChecklist() }); setEditId(i.id); setOpen(true); };

  const setItemStatus = (idx, status) => setForm((f) => ({
    ...f,
    checklist: f.checklist.map((c, j) => (j === idx ? { ...c, status: c.status === status ? "pending" : status } : c)),
  }));

  const upload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      setForm((f) => ({ ...f, photos: [...f.photos, data.path] }));
      toast.success("Photo uploaded");
    } catch (e) { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.title || !form.client_name) return toast.error("Title and client are required");
    try {
      if (editId) await api.put(`/inspections/${editId}`, form);
      else await api.post("/inspections", form);
      toast.success(editId ? "Inspection updated" : "Inspection scheduled");
      setOpen(false); load();
    } catch (e) { toast.error("Could not save"); }
  };

  return (
    <div className="p-8">
      <PageHeader title={user.role === "client" ? "My Inspections" : "Inspections"} subtitle="Field Service">
        {canEdit && <Button onClick={openNew} className="rounded-sm bg-primary hover:bg-primary/90 gap-2" data-testid="new-inspection-btn"><Plus size={16} /> New Inspection</Button>}
      </PageHeader>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {list.map((i) => (
          <div key={i.id} className="bg-card border border-border rounded-sm p-5 flex flex-col animate-fade-up" data-testid={`inspection-card-${i.id}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
              <h3 className="font-heading font-bold text-secondary leading-tight">{i.title}</h3>
              <Badge className={`rounded-sm capitalize shrink-0 ${color[i.status]}`}>{i.status.replace("_", " ")}</Badge>
            </div>
            <p className="text-sm text-muted-foreground">{i.client_name} · {i.equipment_type}</p>
            {i.site_address && <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><MapPin size={12} /> {i.site_address}</p>}
            {i.scheduled_date && <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><Calendar size={12} /> {i.scheduled_date}</p>}
            {i.technician_name && <p className="text-xs text-muted-foreground mt-1">Tech: {i.technician_name}</p>}
            {i.findings && <p className="text-sm mt-2 text-secondary">{i.findings}</p>}
            {i.checklist?.length > 0 && (() => {
              const passed = i.checklist.filter((c) => c.status === "pass").length;
              const failed = i.checklist.filter((c) => c.status === "fail").length;
              return (
                <div className="flex items-center gap-2 mt-3 text-xs" data-testid={`audit-summary-${i.id}`}>
                  <ListChecks size={14} className="text-secondary" />
                  <span className="text-green-600 font-semibold">{passed} pass</span>
                  <span className="text-primary font-semibold">{failed} fail</span>
                  <span className="text-muted-foreground">/ {i.checklist.length} items</span>
                </div>
              );
            })()}
            {i.photos?.length > 0 && (
              <div className="flex gap-2 mt-3 flex-wrap">
                {i.photos.map((p, k) => <img key={k} src={fileUrl(p)} alt="inspection" className="w-14 h-14 object-cover rounded-sm border border-border" />)}
              </div>
            )}
            {canEdit && <Button variant="outline" size="sm" onClick={() => openEdit(i)} className="rounded-sm mt-4 self-start" data-testid={`edit-inspection-${i.id}`}>Update</Button>}
          </div>
        ))}
        {list.length === 0 && <p className="col-span-full text-center py-12 text-muted-foreground">No inspections yet.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editId ? "Update Inspection" : "New Inspection"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs uppercase">Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-sm mt-1" data-testid="insp-title" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase">Client</Label><Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} className="rounded-sm mt-1" data-testid="insp-client" /></div>
              <div><Label className="text-xs uppercase">Equipment</Label><Input value={form.equipment_type} onChange={(e) => setForm({ ...form, equipment_type: e.target.value })} className="rounded-sm mt-1" /></div>
            </div>
            <div><Label className="text-xs uppercase">Site Address</Label><Input value={form.site_address} onChange={(e) => setForm({ ...form, site_address: e.target.value })} className="rounded-sm mt-1" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs uppercase">Scheduled</Label><Input type="date" value={form.scheduled_date} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })} className="rounded-sm mt-1" /></div>
              <div><Label className="text-xs uppercase">Next Due</Label><Input type="date" value={form.next_due_date} onChange={(e) => setForm({ ...form, next_due_date: e.target.value })} className="rounded-sm mt-1" /></div>
              <div><Label className="text-xs uppercase">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}><SelectTrigger className="rounded-sm mt-1" data-testid="insp-status"><SelectValue /></SelectTrigger><SelectContent>{STATUS.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent></Select>
              </div>
            </div>
            <div><Label className="text-xs uppercase">Findings</Label><Textarea value={form.findings} onChange={(e) => setForm({ ...form, findings: e.target.value })} className="rounded-sm mt-1" data-testid="insp-findings" /></div>
            <div>
              <Label className="text-xs uppercase flex items-center gap-1"><ListChecks size={12} /> Safety Audit Checklist</Label>
              <div className="mt-2 border border-border rounded-sm divide-y divide-border" data-testid="audit-checklist">
                {form.checklist.map((c, idx) => (
                  <div key={idx} className="flex items-center gap-3 px-3 py-2" data-testid={`checklist-item-${idx}`}>
                    <span className="flex-1 text-sm text-secondary">{c.label}</span>
                    <button type="button" onClick={() => setItemStatus(idx, "pass")} data-testid={`checklist-pass-${idx}`}
                      className={`w-7 h-7 rounded-sm flex items-center justify-center border transition-colors ${c.status === "pass" ? "bg-green-600 text-white border-green-600" : "border-border text-muted-foreground hover:border-green-600"}`}>
                      <Check size={15} />
                    </button>
                    <button type="button" onClick={() => setItemStatus(idx, "fail")} data-testid={`checklist-fail-${idx}`}
                      className={`w-7 h-7 rounded-sm flex items-center justify-center border transition-colors ${c.status === "fail" ? "bg-primary text-white border-primary" : "border-border text-muted-foreground hover:border-primary"}`}>
                      <X size={15} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase flex items-center gap-1"><ImageIcon size={12} /> Photos</Label>
              <Input type="file" accept="image/*" onChange={upload} disabled={uploading} className="rounded-sm mt-1" data-testid="insp-photo-upload" />
              {form.photos.length > 0 && <div className="flex gap-2 mt-2 flex-wrap">{form.photos.map((p, k) => <img key={k} src={fileUrl(p)} alt="up" className="w-12 h-12 object-cover rounded-sm border" />)}</div>}
            </div>
          </div>
          <DialogFooter><Button onClick={save} className="rounded-sm bg-primary hover:bg-primary/90" data-testid="save-inspection-btn">{editId ? "Update" : "Create"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
